import gql from 'graphql-tag';

/**
 * NOTE: We will always fetch only active voters,
 * and hence the hardcoded filter for the 'flg_inactive' field on all queries
 */

const VoterFragment = gql`
  fragment VoterFragment on ri_apps_prd_voters {
    voter_id
    state_voter_id
    first_name
    last_name
    dob
    age_range
    state
    reg_address1
    voter_status
    mobile_number
  }
`;

const VoterCombFragment = gql`
  fragment VoterCombFragment on ri_apps_prd_voters_comb {
    voter_id
    state_voter_id
    first_name
    last_name
    full_name
    dob
    age_range
    reg_city
    state
    reg_address1
    reg_address2
    reg_zip5
    voter_status
    mobile_number
    reg_zip5
    mail_address1
    mail_address2
    mail_city
    mail_zip5
    mail_state
    rnc_regid
    reg_party
    modeled_party: calcparty
    state_leg_upper_district
    state_leg_lower_district
    county
    precinct
    # Likeliness is DERIVED from these, not read from the vendor columns -- see
    # likelinessScore in sections/voters/components/voting-history-options.js.
    # ⚠️ This list is the election window and moves every cycle.
    vh24g
    vh22g
    vh20g
    vh18g
    vh24p
    vh22p
    vh20p
    vh18p
    voters_comb_org_voter_tags(where: $tagFilter) {
      org_voter_tag_id
      org_voter_tags_c_tag {
        c_tag_id
        title
      }
      org_voter_tags_org_tag {
        org_tag_id
        title
      }
    }
  }
`;

export const GET_ALL_VOTERS = gql`
  query GetVoters(
    $filters: ri_apps_prd_voters_comb_bool_exp = {}
    $limit: Int
    $offset: Int
    $tagFilter: ri_apps_prd_org_voter_tags_bool_exp = {}
  ) {
    rows: ri_apps_prd_voters_comb(
      where: { _and: [$filters] }
      limit: $limit
      offset: $offset
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      ...VoterCombFragment
    }
    aggregate: ri_apps_prd_voters_comb_aggregate(where: { _and: [$filters] }) {
      aggregate {
        count
      }
    }
  }
  ${VoterCombFragment}
`;

/**
 * Voter detail for a CSV export, looked up by the ids already known client-side.
 *
 * **The `$where` must always pin a state.** `voters_comb` is a UNION ALL view over the
 * per-state child tables, so a bare `voter_id: { _in: [...] }` reads every state in the
 * union - catastrophic, not merely slow. Callers pass
 * `_or: [{ state: { _eq }, voter_id: { _in } }, ...]`, one branch per state, exactly as
 * `useRepliesReport` does. Taking a whole `bool_exp` rather than an id list is deliberate:
 * there is no shape of this query that is safe without the caller supplying a state.
 *
 * Deliberately not `GET_ALL_VOTERS`: that one pulls `voters_comb_org_voter_tags` through
 * `VoterCombFragment` and needs a `$tagFilter` an export has no use for. The column set
 * matches the Replies report, so the two files line up for the client.
 *
 * Unlike the queries above this one does not exclude inactive voters. It enriches people
 * who demonstrably answered a survey question; dropping one because their registration
 * later went inactive would silently lose a row that really happened.
 */
/**
 * Just the districts a voter lives in, for splitting question responses by district.
 *
 * Deliberately narrow: GET_VOTERS_FOR_EXPORT pulls twenty columns this has no use for, and the
 * lookup runs over `voters_comb`, a UNION ALL across the per-state child tables -- so it must pin
 * a state or it reads all fifty.
 */
export const GET_VOTER_DISTRICTS = gql`
  query GetVoterDistricts($where: ri_apps_prd_voters_comb_bool_exp!) {
    rows: ri_apps_prd_voters_comb(where: $where) {
      voter_id
      state
      congressional_district
      state_leg_lower_district
      state_leg_upper_district
    }
  }
`;

export const GET_VOTERS_FOR_EXPORT = gql`
  query GetVotersForExport($where: ri_apps_prd_voters_comb_bool_exp!) {
    rows: ri_apps_prd_voters_comb(where: $where) {
      voter_id
      rnc_regid
      state_voter_id
      first_name
      last_name
      mobile_number
      reg_address1
      reg_address2
      reg_city
      reg_zip5
      state
      reg_party
      modeled_party: calcparty
      state_leg_upper_district
      state_leg_lower_district
      county
      precinct
      # Likeliness is DERIVED from these, not read from the vendor columns -- see
      # likelinessScore in sections/voters/components/voting-history-options.js.
      # ⚠️ This list is the election window and moves every cycle.
      vh24g
      vh22g
      vh20g
      vh18g
      vh24p
      vh22p
      vh20p
      vh18p
    }
  }
`;

export const GET_ALL_VOTERS_WITH_BILLS = gql`
  query GetVotersWithBills(
    $filters: ri_apps_prd_voters_comb_bool_exp = {}
    $limit: Int
    $offset: Int
    $orgId: Int!
    $tagFilter: ri_apps_prd_org_voter_tags_bool_exp = {}
  ) {
    rows: ri_apps_prd_voters_comb(
      where: { _and: [$filters] }
      limit: $limit
      offset: $offset
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      ...VoterCombFragment
      voters_comb_org_voter_bills(
        where: { organization_id: { _eq: $orgId } }
        order_by: [{ created_at: desc }]
      ) {
        org_voter_bill_id
        stance
        org_voter_bills_bill_metadata {
          id
          bill_number
        }
      }
    }
    aggregate: ri_apps_prd_voters_comb_aggregate(where: { _and: [$filters] }) {
      aggregate {
        count
      }
    }
  }
  ${VoterCombFragment}
`;

export const GET_VOTERS = gql`
  query GetVoters(
    $filters: ri_apps_prd_voters_comb_bool_exp = {}
    $limit: Int
    $offset: Int
    $tagFilter: ri_apps_prd_org_voter_tags_bool_exp = {}
  ) {
    rows: ri_apps_prd_voters_comb(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
      limit: $limit
      offset: $offset
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      ...VoterCombFragment
    }
    aggregate: ri_apps_prd_voters_comb_aggregate(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
  ${VoterCombFragment}
`;

export const GET_VOTERS_OLD = gql`
  query GetVoters($filters: ri_apps_prd_voters_bool_exp, $limit: Int, $offset: Int) {
    rows: ri_apps_prd_voters(
      where: $filters
      limit: $limit
      offset: $offset
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      ...VoterFragment
    }
    aggregate: ri_apps_prd_voters_aggregate(where: $filters) {
      aggregate {
        count
      }
    }
  }
  ${VoterFragment}
`;

export const DISTINCT_VOTER_HOUSEHOLDS = gql`
  query DistinctVoterHouseholds($filters: ri_apps_prd_voters_comb_bool_exp = {}) {
    distinct: ri_apps_prd_voters_comb_aggregate(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
      distinct_on: [household_id]
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const AGGREGATE_VOTERS = gql`
  query AggregateVoters($filters: ri_apps_prd_voters_comb_bool_exp = {}) {
    aggregate: ri_apps_prd_voters_comb_aggregate(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_MANUAL_VOTERS = gql`
  query GetManualVoters($filters: ri_apps_prd_voters_comb_bool_exp = {}) {
    rows: ri_apps_prd_voters_comb(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
    ) {
      voter_id
      household_id
      reg_lat
      reg_long
    }
  }
`;

export const GET_VOTERS_LOCATIONS = gql`
  query GetVoters($filters: ri_apps_prd_voters_comb_bool_exp = {}, $limit: Int, $offset: Int) {
    rows: ri_apps_prd_voters_comb(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
      limit: $limit
      offset: $offset
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      voter_id
      reg_lat
      reg_long
      reg_city
      precinct
      reg_state
    }
    aggregate: ri_apps_prd_voters_comb_aggregate(
      where: {
        _and: [
          $filters
          { flg_inactive: { _eq: "N" } }
          { _not: { voters_comb_org_no_contact_voter: {} } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

const VoterDetailsFragment = gql`
  fragment VoterDetailsFragment on ri_apps_prd_voters_comb {
    voter_id
    state_voter_id
    first_name
    last_name
    dob
    age_range
    state
    reg_address1
    voter_status
    mobile_number
    middle_name
    children
    precinct
    precinctcode
    phone
    sex
    voter_general_likeliness
    veteran
    state_leg_upper_district
    state_leg_lower_district
    school_district
    salesforce_id
    rnc_regid
    rnc_id
    registered_party_rollup
    reg_zip5
    reg_unit_type
    reg_state
    reg_st_type
    reg_st_prefix
    reg_st_post
    reg_st_name
    reg_registration_date
    reg_party
    reg_long
    reg_location
    reg_lat
    reg_houses_fx
    reg_house_number
    reg_city
    reg_address2
    primary_voter_likeliness
    pref_voting_method
    name_suffix
    modeled_party: calcparty
    marital_status
    income_code
    household_id
    education_level
    employment_code
    voters_comb_employment {
      name
    }
    county
    congressional_district
    city_council
    change_of_address_date
    second_amendment_advocate
    potential_donor_flag
    social_leaning
    fiscal_leaning
    abortion
    mobile_reliability
    vh27p
    vh27g
    vh26p
    vh26g
    vh25p
    vh25g
    vh24g
    vh24p
    vh24pp
    vh23g
    vh23p
    vh22g
    vh22p
    vh21g
    vh21p
    vh20g
    vh20p
    vh20pp
    vh19g
    vh19p
    vh18g
    vh18p
    vh17g
    vh17p
    vh16g
    vh16p
    vh16pp
    vh15g
    vh15p
    vh14g
    vh14p
    vh13g
    vh13p
    vh12g
    vh12p
    vh12pp
    vh11g
    vh11p
    vh10g
    vh10p
    vh09g
    vh09p
    vh08g
    vh08p
    vh08pp
    vh07g
    vh07p
    vh06g
    vh06p
    voters_comb_ri_registered_infogroup {
      rnc_regid
      firstname
      lastname
      gender
      age
      housenumber
      streetpredirectional
      streetname
      streetsuffix
      streetpostdirectional
      unittype
      unitnumber
      city
      zipcode
      countyname
      areacode
      phonenumber
      ind_politicalparty
      ind_vendorethnicity
      ind_vendorreligion
      hh_presenceofchildren
      hh_income
      hh_purchasingpowerincome
      hh_wealthfinder
      hh_donor_political
      hh_outdoor_enthusiast
      hh_hunting
      hh_lhi_politically_conservative
      hh_lhi_liberal
      hh_tw_union_member
      state
    }
    voters_comb_absentee {
      absentee_mail_date
      absentee_request_date
      absentee_return_date
      rowupdatedatetime
    }
    voters_comb_emails {
      email_addr
    }
    voters_comb_personicx {
      lifestage_cluster
      personicx_lifestage_description {
        name
        description
      }
    }
  }
`;

export const GET_VOTER_DETAILS = gql`
  query GetVoterDetails($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      ...VoterDetailsFragment
    }
  }
  ${VoterDetailsFragment}
`;

export const GET_VOTERS_FOR_MAP = gql`
  query GetVotersForMap($where: ri_apps_prd_voters_comb_bool_exp = {}) {
    rows: ri_apps_prd_voters_comb(
      where: $where # order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      voter_id
      first_name
      last_name
      reg_address1
      reg_address2
      reg_city
      state
      reg_zip5
      rnc_regid
      reg_lat
      reg_long
      reg_party
      calcparty
      sex
      age_range
      reg_registration_date
    }
  }
`;

export const GET_ALL_VOTERS_FOR_SEARCH_REST = gql`
  query GetVotersForSearch(
    $filters: ri_apps_prd_voters_comb_bool_exp = {}
    $limit: Int
    $offset: Int
  ) {
    data(filters: $filters, limit: $limit, offset: $offset)
      @rest(
        type: "Voters"
        path: "/crm/voter-search?filters={args.filters}&limit={args.limit}&offset={args.offset}"
        method: "GET"
      ) {
      rows {
        voter_id
        state_voter_id
        first_name
        last_name
        full_name
        dob
        age_range
        reg_city
        state
        reg_address1
        reg_address2
        reg_zip5
        voter_status
        mobile_number
        landline
        reg_zip5
        mail_address1
        mail_address2
        mail_city
        mail_zip5
        mail_state
        rnc_regid
        reg_party
        modeled_party: calcparty
        voters_comb_org_voter_tags {
          org_voter_tag_id
          org_voter_tags_c_tag {
            c_tag_id
            title
          }
          org_voter_tags_org_tag {
            org_tag_id
            title
          }
        }
        voters_comb_org_no_contact_voter {
          organization_id
        }
        voters_comb_org_voter_bills {
          org_voter_bill_id
          stance
          org_voter_bills_bill_metadata {
            id
            bill_number
          }
        }
      }
      aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

export const GET_VOTERS_FOR_SMS_HISTORIES = gql`
  query GetVotersForSmsHistories($where: ri_apps_prd_voters_comb_bool_exp!) {
    rows: ri_apps_prd_voters_comb(where: $where) {
      voter_id
      first_name
      last_name
      state
      reg_address1
      reg_address2
      reg_city
      reg_zip5
    }
  }
`;

export const GET_VOTER_ADDRESS = gql`
  query GetVoterAddress($state: bpchar!, $voter_id: Int!) {
    rows: ri_apps_prd_voters_comb(where: { state: { _eq: $state }, voter_id: { _eq: $voter_id } }) {
      voter_id
      first_name
      last_name
      reg_address1
      reg_address2
      reg_city
      reg_zip5
      state
    }
  }
`;

export const GET_VOTER_UPDATES = gql`
  query GetVoterUpdates($where: ri_apps_prd_voter_updates_bool_exp!) {
    voter_updates: ri_apps_prd_voter_updates(where: $where) {
      voter_updates_id
      voter_id
      voter_updates_organization {
        organization_id
        name
      }
      first_name
      last_name
      mobile_number
      is_mobile_primary
      email_address
      phone
      voter_updates_user {
        user_id
        first_name
        last_name
      }
      created_date
    }
  }
`;

export const ADD_VOTER_CONTACT_UPDATE = gql`
  mutation AddVoterContactUpdate(
    $voter_id: Int!
    $organization_id: Int!
    $user_id: Int!
    $mobile_number: String
    $email_address: String
    $phone: String
  ) {
    insert_ri_apps_prd_voter_updates_one(
      object: {
        voter_id: $voter_id
        organization_id: $organization_id
        user_id: $user_id
        mobile_number: $mobile_number
        email_address: $email_address
        phone: $phone
      }
    ) {
      voter_updates_id
      voter_id
      mobile_number
      email_address
      phone
      created_date
    }
  }
`;

export const UPDATE_VOTER_CONTACT_UPDATE = gql`
  mutation UpdateVoterContactUpdate(
    $voter_updates_id: Int!
    $mobile_number: String
    $email_address: String
    $phone: String
  ) {
    update_ri_apps_prd_voter_updates_by_pk(
      pk_columns: { voter_updates_id: $voter_updates_id }
      _set: { mobile_number: $mobile_number, email_address: $email_address, phone: $phone }
    ) {
      voter_updates_id
      mobile_number
      email_address
      phone
    }
  }
`;

export const DELETE_VOTER_CONTACT_UPDATE = gql`
  mutation DeleteVoterContactUpdate($voter_updates_id: Int!) {
    delete_ri_apps_prd_voter_updates_by_pk(voter_updates_id: $voter_updates_id) {
      voter_updates_id
    }
  }
`;

// Marks an org-curated (secondary) mobile number as the primary contact number for a voter.
// Primary is exclusive per voter+org (radio semantics): the `reset` field first clears any
// existing 'Y' to 'N', then `set` marks the target row 'Y' (Hasura runs mutation fields in
// order). A number is only used for texting/calling tasks when is_mobile_primary = 'Y'.
export const SET_VOTER_MOBILE_PRIMARY = gql`
  mutation SetVoterMobilePrimary(
    $voter_updates_id: Int!
    $voter_id: Int!
    $organization_id: Int!
  ) {
    reset: update_ri_apps_prd_voter_updates(
      where: {
        voter_id: { _eq: $voter_id }
        organization_id: { _eq: $organization_id }
        is_mobile_primary: { _eq: "Y" }
      }
      _set: { is_mobile_primary: "N" }
    ) {
      affected_rows
    }
    set: update_ri_apps_prd_voter_updates_by_pk(
      pk_columns: { voter_updates_id: $voter_updates_id }
      _set: { is_mobile_primary: "Y" }
    ) {
      voter_updates_id
      is_mobile_primary
    }
  }
`;

// Bulk mark selected contact-update rows as the primary mobile number. Exclusive per voter+org:
// `reset` first clears any existing 'Y' for every affected voter, then `set` marks the selected
// rows 'Y'. Callers must pass at most one voter_updates_id per voter (a voter has one primary).
export const MARK_VOTER_UPDATES_PRIMARY = gql`
  mutation MarkVoterUpdatesPrimary(
    $organization_id: Int!
    $voter_ids: [Int!]!
    $voter_updates_ids: [Int!]!
  ) {
    reset: update_ri_apps_prd_voter_updates(
      where: {
        organization_id: { _eq: $organization_id }
        voter_id: { _in: $voter_ids }
        is_mobile_primary: { _eq: "Y" }
      }
      _set: { is_mobile_primary: "N" }
    ) {
      affected_rows
    }
    set: update_ri_apps_prd_voter_updates(
      where: { voter_updates_id: { _in: $voter_updates_ids } }
      _set: { is_mobile_primary: "Y" }
    ) {
      affected_rows
    }
  }
`;

// Clears every org-curated primary mobile flag for a voter+org. Used when the manager selects the
// voter-file number as primary: with no secondary marked 'Y', the file number is the effective
// contact number for texting/calling tasks again.
export const CLEAR_VOTER_MOBILE_PRIMARY = gql`
  mutation ClearVoterMobilePrimary($voter_id: Int!, $organization_id: Int!) {
    update_ri_apps_prd_voter_updates(
      where: {
        voter_id: { _eq: $voter_id }
        organization_id: { _eq: $organization_id }
        is_mobile_primary: { _eq: "Y" }
      }
      _set: { is_mobile_primary: "N" }
    ) {
      affected_rows
    }
  }
`;

// Section-specific queries for progressive loading

// Basic voter information query
export const GET_VOTER_BASIC_INFO = gql`
  query GetVoterBasicInfo($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      state_voter_id
      first_name
      last_name
      middle_name
      name_suffix
      dob
      age_range
      sex
      state
      county
      precinct
      precinctcode
      state_leg_upper_district
      state_leg_lower_district
      stateleglowersubdistrict
      congressional_district
      mediamarket
      household_id
      children
      previousstate
    }
  }
`;

// Household members query — other registered voters sharing the household_id
export const GET_VOTER_HOUSEHOLD = gql`
  query GetVoterHousehold(
    $membersWhere: ri_apps_prd_voters_comb_bool_exp!
    $countWhere: ri_apps_prd_voters_comb_bool_exp!
  ) {
    members: ri_apps_prd_voters_comb(
      where: $membersWhere
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      voter_id
      first_name
      last_name
      age_range
      reg_party
      modeled_party: calcparty
      state
    }
    household_count: ri_apps_prd_voters_comb_aggregate(where: $countWhere) {
      aggregate {
        count
      }
    }
  }
`;

// Address information query
export const GET_VOTER_ADDRESS_INFO = gql`
  query GetVoterAddressInfo($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      mobile_number
      mobile_reliability
      phone
      reg_address1
      reg_address2
      reg_city
      reg_state
      reg_zip5
      mail_address1
      mail_address2
      mail_city
      mail_state
      mail_zip5
      change_of_address_date
      voters_comb_emails {
        email_addr
      }
    }
  }
`;

// Voting information query
export const GET_VOTER_VOTING_INFO = gql`
  query GetVoterVotingInfo($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      rnc_regid
      rnc_id
      state_voter_id
      reg_registration_date
      reg_party
      previousregparty
      modeled_party: calcparty
      # primary_voter_likeliness / voter_general_likeliness deliberately not selected: the profile
      # derives likeliness from GET_VOTER_VOTING_HISTORY's vh* columns instead, so the figure on
      # screen is the same one the search filters on rather than the vendor's parallel opinion.
      pref_voting_method
      # The state's own registration standing ('A'/'I'), from the child tables via voters_comb.
      # ⚠️ Hasura must expose this column on voters_comb for the role, or this whole query fails
      # validation and the profile shows an error rather than just missing the field.
      voterstatus
    }
  }
`;

// Absentee/early-vote records — fetched on its own (not joined into the heavy
// voting-history query). Serves both the propensity tab (most recent record) and
// the persona/previous-elections logic (all records, by electionkey).
// $absenteeState repeats $state as the absentee table's own type (varchar, not bpchar). Without it
// Postgres can only join on a cast and scans every state's partition: 1.8 s per voter on prod.
export const GET_VOTER_ABSENTEE = gql`
  query GetVoterAbsentee($id: Int, $state: bpchar, $absenteeState: String) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      voters_comb_absentee(where: { state: { _eq: $absenteeState } }) {
        electionkey
        absentee_request_date
        absentee_mail_date
        absentee_return_date
        absentee_earlyvoted_date
        rowupdatedatetime
      }
    }
  }
`;

// Voting history query
export const GET_VOTER_VOTING_HISTORY = gql`
  query GetVoterVotingHistory($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      vh27p
      vh27g
      vh26p
      vh26g
      vh25p
      vh25g
      vh24g
      vh24p
      vh24pp
      vh23g
      vh23p
      vh22g
      vh22p
      vh21g
      vh21p
      vh20g
      vh20p
      vh20pp
      vh19g
      vh19p
      vh18g
      vh18p
      vh17g
      vh17p
      vh16g
      vh16p
      vh16pp
      vh15g
      vh15p
      vh14g
      vh14p
      vh13g
      vh13p
      vh12g
      vh12p
      vh12pp
      vh11g
      vh11p
      vh10g
      vh10p
      vh09g
      vh09p
      vh08g
      vh08p
      vh08pp
      vh07g
      vh07p
      vh06g
      vh06p
    }
  }
`;

// Election VH field mapping query — used to resolve electionkey → electionvhfield for absentee status display
export const GET_ELECTION_VH_FIELDS = gql`
  query GetElectionVhFields {
    election_keys: ri_apps_prd_ri_electionkeys(
      where: { electionvhfield: { _is_null: false }, electionyear: { _gte: "2004" } }
    ) {
      electionkey
      electionvhfield
      electiontype
    }
  }
`;

// Demographics information query (modeled columns on voters_comb; no infogroup)
export const GET_VOTER_PROFILE_INFO = gql`
  query GetVoterProfileInfo($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      ethnicgroupmodeled
      religionmodeled
      languagemodeled
      education_level
      income_code
      employment_code
    }
  }
`;

// Personicx information query
export const GET_VOTER_PERSONICX_INFO = gql`
  query GetVoterPersonicxInfo($id: Int, $state: bpchar) {
    voter_details: ri_apps_prd_voters_comb(
      where: { _and: [{ voter_id: { _eq: $id } }, { state: { _eq: $state } }] }
    ) {
      voter_id
      voters_comb_personicx {
        lifestage_cluster
        personicx_lifestage_description {
          name
          description
        }
      }
    }
  }
`;

export const ADD_VOTER_UPDATE = gql`
  mutation AddVoterUpdate(
    $voter_id: Int
    $notes: String
    $first_name: String
    $last_name: String
    $mobile_number: String
    $email_address: String
    $organization_id: Int
    $user_id: Int
  ) {
    insert_ri_apps_prd_voter_updates(
      objects: {
        voter_id: $voter_id
        notes: $notes
        first_name: $first_name
        last_name: $last_name
        mobile_number: $mobile_number
        email_address: $email_address
        organization_id: $organization_id
        user_id: $user_id
      }
    ) {
      affected_rows
    }
  }
`;
